/**
 * Regex matching for tenant configs that behaves the same in JavaScript and in Python (backend).
 * Keep in sync with backend/internal_data_processing/utils/portable_regex.py (both are tested against the same cases).
 *
 * Allowed: literals, `.`, `^`, `$`, `|`, `* + ? {n} {n,} {n,m}` (and lazy `?`), `[...]` classes, groups `( )`,
 * `(?: )`, lookahead `(?= )` `(?! )`, and the escapes `\d \D \w \W \s \S \b \B \t \n \r` plus backslash + punctuation.
 * Matching is a case-sensitive search (not a full match); use ^ and $ to anchor.
 * Not allowed: named groups, lookbehind, inline flags, backreferences, \A \Z \p \u \x, and a quantified group
 * that itself contains a quantifier (catastrophic backtracking).
 */
export const MAX_PATTERN_LENGTH = 200;
export const MAX_QUANTIFIERS = 5;
const ESCAPES = "dDwWsSbBtnr";
// eslint-disable-next-line security/detect-unsafe-regex -- fixed short pattern, only run on a slice starting at "{"
const QUANTIFIER = /^\{\d+(,\d*)?\}/;

/** Returns an error message when the pattern is not portable between JavaScript and Python, else null. */
export function validatePortableRegex(pattern: unknown): string | null {
    if (typeof pattern !== "string" || pattern === "") {
        return "Regex is empty";
    }
    if (pattern.length > MAX_PATTERN_LENGTH) {
        return `Regex is longer than ${MAX_PATTERN_LENGTH} characters`;
    }

    let inClass = false;
    let depth = 0;
    let quantifiers = 0;
    let justClosedGroup = false;
    let i = 0;
    while (i < pattern.length) {
        const c = pattern[i];
        let closedGroup = false;
        if (c === "\\") {
            if (i + 1 >= pattern.length) {
                return "Regex ends with a single backslash";
            }
            const next = pattern[i + 1];
            if (/[A-Za-z0-9_]/.test(next)) {
                if (!ESCAPES.includes(next)) {
                    return `Escape \\${next} is not supported (it differs between Python and JavaScript)`;
                }
                if (inClass && "bB".includes(next)) {
                    return `Escape \\${next} is not supported inside [...]`;
                }
            }
            i += 1; // skip the escaped character, the loop end skips the backslash
        } else if (inClass) {
            if (c === "]") {
                inClass = false;
            }
        } else if (c === "[") {
            inClass = true;
            if (pattern[i + 1] === "^") {
                i += 1;
            }
            if (pattern[i + 1] === "]") {
                return "Empty or leading ] in [...] is not supported";
            }
        } else if (c === "(") {
            if (pattern[i + 1] === "?") {
                if (![":", "=", "!"].includes(pattern[i + 2])) {
                    return "Only (?:...), (?=...) and (?!...) groups are supported";
                }
                i += 2;
            }
            depth += 1;
        } else if (c === ")") {
            if (depth === 0) {
                return "Unbalanced )";
            }
            depth -= 1;
            closedGroup = true;
        } else if (c === "*" || c === "+" || c === "?" || c === "{") {
            if (c === "{") {
                const m = QUANTIFIER.exec(pattern.slice(i));
                if (!m) {
                    return "Use \\{ for a literal {";
                }
                i += m[0].length - 1;
            }
            quantifiers += 1;
            if (quantifiers > MAX_QUANTIFIERS) {
                return `At most ${MAX_QUANTIFIERS} quantifiers (* + ? {}) are supported`;
            }
            if (c !== "?" && justClosedGroup) {
                return "Repeating a group with * + or {} is not supported, use simple alternatives like ^(RR|QAS)-";
            }
            // lazy modifier
            if (c !== "?" && pattern[i + 1] === "?") {
                i += 1;
            }
        }
        i += 1;
        justClosedGroup = closedGroup;
    }
    if (inClass) {
        return "Unclosed [...]";
    }
    if (depth > 0) {
        return "Unclosed (";
    }
    try {
        // eslint-disable-next-line no-new, security/detect-non-literal-regexp -- the pattern was validated above
        new RegExp(pattern);
    } catch (e) {
        return `Invalid regex: ${(e as Error).message}`;
    }
    return null;
}

/** True when the pattern is portable and matches somewhere in String(value). Invalid patterns never match. */
export function portableRegexSearch(pattern: string, value: unknown): boolean {
    if (validatePortableRegex(pattern) !== null) {
        return false;
    }
    // eslint-disable-next-line security/detect-non-literal-regexp -- the pattern was validated above
    return new RegExp(pattern).test(String(value));
}
